"use client";

import Link from "next/link";
import type { ActiveLandingBanner } from "@/features/landing-banners/_types/landing-banner-ui-types";

interface PromoBannerProps {
  banner: ActiveLandingBanner | null;
}

/**
 * Fixed-top landing-page promo banner. Renders whatever banner the server
 * resolved for the single promo slot — a standalone landing banner or the
 * active whitepaper banner — and returns null when nothing is live (so
 * `--promo-offset` stays unset and the layout sits flush to the top).
 *
 * Full-width, brand-coloured, occupies the height reserved by the
 * `--promo-offset` CSS variable set on the landing-page wrapper; the Navbar and
 * Hero read the same variable and shift down automatically. External links open
 * in a new tab.
 *
 * @param props.banner Resolved banner payload, or null when nothing is live.
 */
export function PromoBanner({ banner }: PromoBannerProps) {
  if (!banner) return null;

  const externalProps = banner.external ? { target: "_blank", rel: "noopener noreferrer" } : {};

  return (
    <Link
      href={banner.href}
      aria-label={banner.ariaLabel}
      {...externalProps}
      className="fixed inset-x-0 top-0 z-55 box-border flex h-(--promo-offset) w-full items-center justify-center gap-x-2 gap-y-0.5 bg-(--landing-button-primary-bg) px-3 py-1.5 text-center text-sm font-medium leading-snug text-landing-white transition-colors hover:bg-(--landing-button-primary-hover) focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:px-4 lg:gap-2 lg:px-4 lg:py-0"
    >
      <span className="line-clamp-2 min-w-0 max-w-[calc(100%-1.75rem)] text-pretty sm:max-w-md lg:line-clamp-1 lg:max-w-none">
        {banner.text}
      </span>
      <span className="shrink-0 self-center" aria-hidden="true">
        →
      </span>
    </Link>
  );
}
